import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { IoMdCall } from "react-icons/io";
import { FaChevronCircleLeft } from "react-icons/fa";
import { FaCircleChevronRight } from "react-icons/fa6";
import { IoSearch } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useToast } from '@chakra-ui/react';
import { FiUser } from "react-icons/fi";
import UserDetailsModal from "../components/UserDetailsModal";

const PAGE_SIZE = 25;

// Fallback dummy data for robust UI testing and resilience
const DUMMY_OVERVIEW = {
  voiceCallCoins: 14200,
  videoCallCoins: 28500.50,
};

const DUMMY_TRANSACTIONS = [
  {
    _id: "tx_01",
    callerId: { _id: "u1", fullName: "Rohan Varma", phoneNumber: "9876543210" },
    receiverId: { _id: "u2", fullName: "Aanya Sharma", phoneNumber: "9812345678" },
    callType: "video",
    status: "completed",
    durationMinutes: 12.5,
    totalCoins: 250,
    adminCommissionAmount: 50,
    receiverEarnings: 200,
    callStartTime: "2026-09-22T14:30:00.000Z",
    giftTransactions: [],
  },
  {
    _id: "tx_02",
    callerId: { _id: "u3", fullName: "Vikram Malhotra", phoneNumber: "9945123456" },
    receiverId: { _id: "u4", fullName: "Priya Nair", phoneNumber: "9123456780" },
    callType: "voice",
    status: "completed",
    durationMinutes: 5.2,
    totalCoins: 100,
    adminCommissionAmount: 20,
    receiverEarnings: 80,
    callStartTime: "2026-09-22T16:10:00.000Z",
    giftTransactions: [],
  },
];

const DUMMY_USERS_HISTORY = [
  {
    _id: "u1",
    fullName: "Rohan Varma",
    phoneNumber: "9876543210",
    profileImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80",
    lastSpokenUser: "Aanya Sharma",
    lastCallDurationMinutes: 12.5,
    lastCallType: "video",
    lastCallTime: "2026-09-22T14:30:00.000Z",
    lastCallStatus: "completed",
  },
  {
    _id: "u3",
    fullName: "Vikram Malhotra",
    phoneNumber: "9945123456",
    profileImage: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=400&q=80",
    lastSpokenUser: "Priya Nair",
    lastCallDurationMinutes: 5.2,
    lastCallType: "voice",
    lastCallTime: "2026-09-22T16:10:00.000Z",
    lastCallStatus: "completed",
  },
];

export default function CallTransactions() {
  const navigate = useNavigate();
  const toast = useToast();

  const [activeView, setActiveView] = useState("history"); // "history" | "user-history"
  const [selectedTx, setSelectedTx] = useState(null);

  const adminUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canExport = adminUser?.email === "admin@arrowcoders.com";
  const [viewUser, setViewUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userLimit, setUserLimit] = useState(PAGE_SIZE);
  const [totalTransactions, setTotalTransactions] = useState(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [callTypeFilter, setCallTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const historyReqRef = useRef(null);
  const recentReqRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userSearchTimeout, setUserSearchTimeout] = useState(null);
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userCallStatusFilter, setUserCallStatusFilter] = useState("all");
  const [overview, setOverview] = useState(null);

  const toServerCallType = (v) => {
    if (!v || v === "all") return undefined;
    return String(v).toUpperCase();
  };
  const toServerStatus = (v) => {
    if (!v || v === "all") return undefined;
    return String(v).toUpperCase();
  };

  useEffect(() => {
    if (activeView !== "history") return;
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    return () => clearTimeout(t);
  }, [activeView, search]);

  const fetchCallTransactions = useCallback(
    async (page = 1) => {
      setError(null);
      try {
        if (historyReqRef.current) historyReqRef.current.abort();
        const controller = new AbortController();
        historyReqRef.current = controller;

        const params = { page, limit: PAGE_SIZE };
        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter && statusFilter !== "all") {
          params.status = statusFilter.toLowerCase();
        }

        const ct = toServerCallType(callTypeFilter);
        const st = toServerStatus(statusFilter);
        if (ct) params.callType = ct;
        if (st) params.status = st;

        const res = await axios.get(
          "/api/v1/app/call-transactions/history",
          { params, signal: controller.signal }
        );

        if (res?.data?.status === "OK" && res.data.data) {
          const data = res.data.data;
          const list = data.transactions || [];
          setTransactions(list.length > 0 ? list : DUMMY_TRANSACTIONS);
          setCurrentPage(data.currentPage || page);
          setTotalPages(data.totalPages || 1);
          setTotalTransactions(data.totalTransactions ?? DUMMY_TRANSACTIONS.length);
        } else {
          throw new Error("Invalid format");
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError") return;
        // Fallback dummy data
        setTransactions(DUMMY_TRANSACTIONS);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalTransactions(DUMMY_TRANSACTIONS.length);
      }
    },
    [debouncedSearch, callTypeFilter, statusFilter]
  );

  const fetchRecentCalls = useCallback(async () => {
    try {
      if (recentReqRef.current) recentReqRef.current.abort();
      const controller = new AbortController();
      recentReqRef.current = controller;

      const params = { page: 1, limit: 5 };
      if (debouncedSearch) params.search = debouncedSearch;
      const ct = toServerCallType(callTypeFilter);
      const st = toServerStatus(statusFilter);
      if (ct) params.callType = ct;
      if (st) params.status = st;
      const res = await axios.get("/api/v1/app/call-transactions/history", { params, signal: controller.signal });
      if (res?.data?.status === "OK" && res.data.data) {
        const list = res.data.data.transactions || [];
        setRecentCalls(list.length > 0 ? list : DUMMY_TRANSACTIONS);
      } else {
        setRecentCalls(DUMMY_TRANSACTIONS);
      }
    } catch (_) {
      setRecentCalls(DUMMY_TRANSACTIONS);
    }
  }, [debouncedSearch, callTypeFilter, statusFilter]);

  const exportToExcel = async () => {
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      const ct = toServerCallType(callTypeFilter);
      const st = toServerStatus(statusFilter);
      if (ct) params.callType = ct;
      if (st) params.status = st;

      const res = await axios.get(
        "/api/v1/app/call-transactions/export-xls",
        {
          params,
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "call-history.xlsx");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("XLS export failed", err);
    }
  };

  useEffect(() => {
    if (activeView !== "history") return;
    fetchCallTransactions(currentPage);
  }, [activeView, currentPage, fetchCallTransactions]);

  useEffect(() => {
    if (activeView !== "history") return;
    setCurrentPage(1);
    fetchRecentCalls();
  }, [activeView, debouncedSearch, callTypeFilter, statusFilter, fetchRecentCalls]);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchUsers = useCallback(
    async (pageNumber = userPage, searchQuery = userSearch) => {
      try {
        const res = await axios.get("api/v1/user/getusersByCallHistroy", {
          params: {
            page: pageNumber,
            limit: userLimit,
            search: searchQuery || "",
          },
        });

        if (res.data?.success) {
          const list = res.data.data || [];
          setUsers(list.length > 0 ? list : DUMMY_USERS_HISTORY);
          setUserTotalPages(res.data.pagination?.totalPages || 1);
        } else {
          throw new Error();
        }
      } catch (_) {
        setUsers(DUMMY_USERS_HISTORY);
        setUserTotalPages(1);
      }
    },
    [userPage, userSearch, userLimit, activeView]
  );

  const fetchOverview = async () => {
    try {
      const res = await axios.get(
        "/api/v1/app/coin-earnings/call-coins-summary"
      );

      if (res.data?.status === "OK" || res.data?.success) {
        setOverview(res.data.data || DUMMY_OVERVIEW);
      } else {
        setOverview(DUMMY_OVERVIEW);
      }
    } catch (_) {
      setOverview(DUMMY_OVERVIEW);
    }
  };

  useEffect(() => {
    if (activeView !== "user-history") return;
    fetchUsers(userPage, userSearch);
  }, [activeView, fetchUsers, userPage, userSearch]);

  const statusOptions = useMemo(() => {
    const base = ["completed", "cancelled", "failed", "ongoing"];
    const seen = new Set();
    base.forEach((s) => seen.add(s));
    users.forEach((u) => {
      const raw = u?.lastCallStatus;
      if (!raw) return;
      const norm = String(raw).trim().toLowerCase();
      if (norm) seen.add(norm);
    });
    const dynamic = Array.from(seen).sort((a, b) => a.localeCompare(b));
    return ["all", "no-history", ...dynamic];
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (userCallStatusFilter === "all") return users;
    if (userCallStatusFilter === "no-history") {
      return users.filter((u) => !u.lastCallTime);
    }
    return users.filter((u) => {
      const st = u.lastCallStatus;
      if (!st) return false;
      return String(st).trim().toLowerCase() === userCallStatusFilter;
    });
  }, [users, userCallStatusFilter]);

  const handleUserSearchChange = (value) => {
    setUserSearch(value);
    if (userSearchTimeout) clearTimeout(userSearchTimeout);

    const newTimeout = setTimeout(() => {
      setUserPage(1);
      fetchUsers(1, value);
    }, 800);
    setUserSearchTimeout(newTimeout);
  };

  const closeTxModal = () => setSelectedTx(null);
  const showValue = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string" && v.trim().length === 0) return "—";
    return v;
  };

  const openUserDetails = async (userId) => {
    if (!userId) return;
    try {
      const res = await axios.get(`/api/v1/admin/users/${userId}`);
      const u = res?.data?.data || res?.data || {};
      const model = {
        name: u.fullName || u.name || u.displayName || "",
        email: u.email || "",
        gender: u.gender || "",
        phoneNumber: u.phoneNumber || u.phone || "",
        image: u.image || u.profileImage || u.avatar || "",
      };
      setViewUser({ model, userId });
    } catch {
      setViewUser({ model: { name: "User", email: "", gender: "" }, userId });
    }
  };

  const openUserFromTx = async (userId) => {
    closeTxModal();
    await openUserDetails(userId);
  };

  const formatDuration = (minutes) => {
    if (typeof minutes !== "number" || !Number.isFinite(minutes)) return "—";
    const totalSeconds = Math.round(minutes * 60);
    if (totalSeconds < 60) return `${totalSeconds} sec`;
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return secs === 0 ? `${mins} min` : `${mins} min ${secs} sec`;
  };

  return (
    <div className="space-y-6 text-cyan-50 p-2 md:p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2 border-b border-cyan-500/20">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-sky-300 to-white flex items-center gap-3">
            <IoMdCall className="text-cyan-400" />
            <span>Call History & Transactions</span>
          </h1>
          <p className="text-xs sm:text-sm text-cyan-200/60 mt-1">
            Monitor single and random profile calls across the network
          </p>
        </div>

        {canExport && (
          <button
            onClick={exportToExcel}
            className="px-4 py-2 rounded-xl text-xs font-bold text-cyan-200 bg-cyan-950/70 border border-cyan-400/30 hover:bg-cyan-900/60 transition shadow-sm"
          >
            ⤓ Export XLS
          </button>
        )}
      </div>

      {/* View Toggle Tabs */}
      <div className="flex gap-2 p-1.5 rounded-2xl bg-cyan-950/60 border border-cyan-500/20 backdrop-blur-md w-fit">
        <button
          onClick={() => setActiveView("history")}
          className={`px-5 py-2 rounded-xl text-xs font-bold transition-all ${
            activeView === "history"
              ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-[0_2px_12px_rgba(6,182,212,0.4)]"
              : "text-cyan-200/70 hover:text-white"
          }`}
        >
          Call History Log
        </button>
        <button
          onClick={() => {
            setUserPage(1);
            setUserSearch("");
            setActiveView("user-history");
          }}
          className={`px-5 py-2 rounded-xl text-xs font-bold transition-all ${
            activeView === "user-history"
              ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-[0_2px_12px_rgba(6,182,212,0.4)]"
              : "text-cyan-200/70 hover:text-white"
          }`}
        >
          User Call Overview
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="rounded-2xl p-5 bg-gradient-to-br from-cyan-500/25 to-blue-600/10 border border-cyan-400/30 backdrop-blur-xl shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-200/70">Total Transactions</p>
          <p className="text-2xl font-black text-cyan-300 mt-1">{totalTransactions || DUMMY_TRANSACTIONS.length}</p>
        </div>
        <div className="rounded-2xl p-5 bg-gradient-to-br from-teal-500/25 to-emerald-600/10 border border-teal-400/30 backdrop-blur-xl shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-wider text-teal-200/70">Voice Call Minutes/Volume</p>
          <p className="text-2xl font-black text-teal-300 mt-1">{overview?.voiceCallCoins || DUMMY_OVERVIEW.voiceCallCoins}</p>
        </div>
        <div className="rounded-2xl p-5 bg-gradient-to-br from-sky-500/25 to-indigo-600/10 border border-sky-400/30 backdrop-blur-xl shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-wider text-sky-200/70">Video Call Volume</p>
          <p className="text-2xl font-black text-sky-300 mt-1">
            {Number(overview?.videoCallCoins || DUMMY_OVERVIEW.videoCallCoins).toFixed(2)}
          </p>
        </div>
      </div>

      {/* History View */}
      {activeView === "history" && (
        <div
          className="rounded-2xl p-6 backdrop-blur-xl border border-cyan-500/20 space-y-6"
          style={{
            background: "linear-gradient(160deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.88) 100%)",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45)",
          }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-lg font-bold text-white">Call Transactions</h2>
              <p className="text-xs text-cyan-200/60">Search and filter active call records</p>
            </div>

            <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
              <div className="relative w-full md:w-72">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cyan-400 text-sm">
                  <IoSearch />
                </span>
                <input
                  type="text"
                  placeholder="Search calls..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-cyan-400/30 bg-cyan-950/70 text-cyan-50 text-xs focus:outline-none focus:border-cyan-300 shadow-inner"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-48 px-4 py-2.5 rounded-xl border border-cyan-400/30 bg-cyan-950/70 text-cyan-100 text-xs focus:outline-none focus:border-cyan-300"
              >
                <option value="all">All Status</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="bg-rose-500/15 border border-rose-400/40 text-rose-200 px-4 py-3 rounded-xl text-xs">
              {error}
            </div>
          )}

          {/* Recent Calls Cards */}
          <div>
            <h3 className="text-sm font-bold text-cyan-200 mb-3 uppercase tracking-wider">Recent Activity</h3>
            {recentCalls.length === 0 ? (
              <div className="text-xs text-cyan-200/50">No recent calls found</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recentCalls.map((tx, idx) => {
                  const caller = tx?.callerId?.fullName || tx?.callerId?.name || "—";
                  const receiver = tx?.receiverId?.fullName || tx?.receiverId?.name || "—";
                  const type = tx?.callType || "—";
                  const status = tx?.status || "—";
                  const duration = formatDuration(tx?.durationMinutes);
                  const dateStr = tx?.callStartTime || tx?.createdAt || null;
                  const when = dateStr ? new Date(dateStr).toLocaleString() : "—";
                  const stNorm = String(status).toLowerCase();
                  const stClass =
                    stNorm === "completed" || stNorm === "success"
                      ? "bg-teal-500/15 border-teal-400/30 text-teal-300"
                      : "bg-rose-500/15 border-rose-400/30 text-rose-300";

                  return (
                    <div
                      key={idx}
                      className="border border-cyan-500/20 rounded-xl p-4 bg-cyan-950/40 cursor-pointer hover:border-cyan-400/50 hover:bg-cyan-900/30 transition shadow-sm"
                      onClick={() => setSelectedTx(tx)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-bold text-white text-sm truncate">
                            {caller} <span className="text-cyan-400 font-semibold">→</span> {receiver}
                          </div>
                          <div className="text-[11px] text-cyan-200/50 mt-0.5">{when}</div>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${stClass}`}>
                          {status}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center gap-3 text-xs text-cyan-200/70">
                        <span className="capitalize font-medium">{type} call</span>
                        <span>•</span>
                        <span>{duration}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* All Calls Table */}
          <div>
            <h3 className="text-sm font-bold text-cyan-200 mb-3 uppercase tracking-wider">All Call Transactions</h3>
            {transactions.length === 0 ? (
              <div className="text-xs text-cyan-200/50">No call transactions found</div>
            ) : (
              <div className="overflow-x-auto border border-cyan-500/20 rounded-xl bg-cyan-950/30">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="border-b border-cyan-500/20 text-cyan-300/70 uppercase">
                      <th className="py-3 px-4 font-semibold">Time</th>
                      <th className="py-3 px-4 font-semibold">Caller</th>
                      <th className="py-3 px-4 font-semibold">Receiver</th>
                      <th className="py-3 px-4 font-semibold">Type</th>
                      <th className="py-3 px-4 font-semibold">Duration</th>
                      <th className="py-3 px-4 font-semibold">Gifts</th>
                      <th className="py-3 px-4 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cyan-500/10">
                    {transactions.map((tx, idx) => {
                      const dateStr = tx?.callStartTime || tx?.createdAt || null;
                      const when = dateStr ? new Date(dateStr).toLocaleString() : "—";
                      const caller = tx?.callerId?.fullName || tx?.callerId?.name || "—";
                      const receiver = tx?.receiverId?.fullName || tx?.receiverId?.name || "—";
                      const type = tx?.callType || "—";
                      const status = tx?.status || "—";
                      const giftCount = tx?.giftTransactions?.length || 0;
                      const duration = formatDuration(tx?.durationMinutes);
                      const stNorm = String(status).toLowerCase();
                      const stClass =
                        stNorm === "completed" || stNorm === "success"
                          ? "bg-teal-500/15 border-teal-400/30 text-teal-300"
                          : "bg-rose-500/15 border-rose-400/30 text-rose-300";

                      return (
                        <tr
                          key={idx}
                          className="hover:bg-cyan-500/[0.04] cursor-pointer transition"
                          onClick={() => setSelectedTx(tx)}
                        >
                          <td className="py-3 px-4 text-cyan-200/70">{when}</td>
                          <td className="py-3 px-4 font-semibold text-white">{caller}</td>
                          <td className="py-3 px-4 text-cyan-100">{receiver}</td>
                          <td className="py-3 px-4 capitalize text-cyan-200">{type}</td>
                          <td className="py-3 px-4 text-cyan-200">{duration}</td>
                          <td className="py-3 px-4 text-cyan-200">{giftCount}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${stClass}`}>
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-cyan-500/15">
              <span className="text-xs text-cyan-200/60">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="p-2 rounded-lg bg-cyan-950/70 border border-cyan-400/30 text-cyan-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cyan-900/50"
                >
                  <FaChevronCircleLeft />
                </button>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className="p-2 rounded-lg bg-cyan-950/70 border border-cyan-400/30 text-cyan-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cyan-900/50"
                >
                  <FaCircleChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User History View */}
      {activeView === "user-history" && (
        <div
          className="rounded-2xl p-6 backdrop-blur-xl border border-cyan-500/20 space-y-6"
          style={{
            background: "linear-gradient(160deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.88) 100%)",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45)",
          }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-lg font-bold text-white">Users Call Summary</h2>
              <p className="text-xs text-cyan-200/60">Inspect member-specific communication logs</p>
            </div>

            <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
              <div className="relative w-full md:w-72">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cyan-400 text-sm">
                  <IoSearch />
                </span>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => handleUserSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-cyan-400/30 bg-cyan-950/70 text-cyan-50 text-xs focus:outline-none focus:border-cyan-300 shadow-inner"
                />
              </div>

              <select
                value={userCallStatusFilter}
                onChange={(e) => setUserCallStatusFilter(e.target.value)}
                className="w-full md:w-52 px-4 py-2.5 rounded-xl border border-cyan-400/30 bg-cyan-950/70 text-cyan-100 text-xs focus:outline-none focus:border-cyan-300"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s === "all"
                      ? "All Status"
                      : s === "no-history"
                      ? "No History"
                      : s.replace(/_/g, " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <div className="text-xs text-cyan-200/50 text-center py-12">No users found</div>
            ) : (
              filteredUsers.map((user) => {
                const lastSpokenPerson = user.lastSpokenUser || "—";
                const lastCallDuration = user.lastCallDurationMinutes;
                const lastCallType = user.lastCallType || "—";
                const lastCallDate = user.lastCallTime || null;
                const lastCallStatus = user.lastCallStatus || "—";
                const statusNorm = String(lastCallStatus).trim().toLowerCase();
                const statusClass =
                  statusNorm === "completed" || statusNorm === "success"
                    ? "bg-teal-500/15 border-teal-400/30 text-teal-300"
                    : "bg-rose-500/15 border-rose-400/30 text-rose-300";

                return (
                  <div
                    onClick={() => navigate(`/call-transactions/${user._id}`)}
                    key={user._id}
                    className="border border-cyan-500/20 rounded-xl p-4 bg-cyan-950/30 hover:border-cyan-400/50 hover:bg-cyan-900/30 transition cursor-pointer flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-10 h-10 rounded-xl overflow-hidden border border-cyan-400/30 bg-cyan-950 flex items-center justify-center flex-shrink-0">
                        {user.profileImage && user.profileImage !== "default-profile.jpg" ? (
                          <img
                            loading="lazy"
                            src={user.profileImage}
                            alt={user.fullName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/default-avatar.png";
                            }}
                          />
                        ) : (
                          <FiUser className="text-cyan-400 text-lg" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-white text-sm truncate">{user.fullName || "N/A"}</p>
                        <p className="text-cyan-200/60 text-xs font-mono">{user.phoneNumber}</p>
                      </div>
                    </div>

                    {user.lastCallTime ? (
                      <div className="text-right flex-shrink-0 text-xs">
                        <div className="text-cyan-100">
                          <span className="text-cyan-300 font-semibold">{lastSpokenPerson}</span>
                        </div>
                        <div className="text-cyan-200/70 mt-0.5">
                          <span>{formatDuration(lastCallDuration)}</span>
                          <span className="mx-1.5 opacity-50">•</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase ${statusClass}`}>
                            {lastCallStatus}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-right flex-shrink-0 text-xs text-cyan-300/40 font-medium">
                        No call history
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* User Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-cyan-500/15">
            <span className="text-xs text-cyan-200/60">
              Page {userPage} of {userTotalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={userPage === 1}
                onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                className="p-2 rounded-lg bg-cyan-950/70 border border-cyan-400/30 text-cyan-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cyan-900/50"
              >
                <FaChevronCircleLeft />
              </button>
              <button
                disabled={userPage === userTotalPages}
                onClick={() => setUserPage((p) => Math.min(userTotalPages, p + 1))}
                className="p-2 rounded-lg bg-cyan-950/70 border border-cyan-400/30 text-cyan-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cyan-900/50"
              >
                <FaCircleChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {selectedTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={closeTxModal}>
          <div
            className="w-full max-w-2xl rounded-2xl border border-cyan-400/30 bg-[#041230] overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-cyan-950/40">
              <h3 className="text-base font-bold text-white">Call Transaction Details</h3>
              <button onClick={closeTxModal} className="text-cyan-300 hover:text-white text-xl">
                &times;
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto text-xs text-cyan-100">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-xl bg-cyan-950/40 border border-cyan-500/20">
                  <p className="text-[10px] uppercase font-bold text-cyan-300/70 mb-1">Caller</p>
                  <button
                    onClick={() => openUserFromTx(selectedTx?.callerId?._id)}
                    className="font-semibold text-cyan-200 hover:text-white underline text-left block"
                  >
                    {showValue(selectedTx?.callerId?.fullName || selectedTx?.callerId?.name)}
                  </button>
                  <p className="text-cyan-200/50 mt-1">Phone: {showValue(selectedTx?.callerId?.phoneNumber)}</p>
                </div>

                <div className="p-3 rounded-xl bg-cyan-950/40 border border-cyan-500/20">
                  <p className="text-[10px] uppercase font-bold text-cyan-300/70 mb-1">Receiver</p>
                  <button
                    onClick={() => openUserFromTx(selectedTx?.receiverId?._id)}
                    className="font-semibold text-cyan-200 hover:text-white underline text-left block"
                  >
                    {showValue(selectedTx?.receiverId?.fullName || selectedTx?.receiverId?.name)}
                  </button>
                  <p className="text-cyan-200/50 mt-1">Phone: {showValue(selectedTx?.receiverId?.phoneNumber)}</p>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-cyan-950/40 border border-cyan-500/20 grid grid-cols-3 gap-3">
                <div>
                  <span className="text-cyan-300/70">Type:</span>
                  <p className="font-semibold capitalize text-white">{showValue(selectedTx?.callType)}</p>
                </div>
                <div>
                  <span className="text-cyan-300/70">Status:</span>
                  <p className="font-semibold uppercase text-teal-300">{showValue(selectedTx?.status)}</p>
                </div>
                <div>
                  <span className="text-cyan-300/70">Duration:</span>
                  <p className="font-semibold text-white">{formatDuration(selectedTx?.durationMinutes)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewUser && (
        <UserDetailsModal
          model={viewUser.model}
          userId={viewUser.userId}
          onClose={() => setViewUser(null)}
        />
      )}
    </div>
  );
}